import React, { FC, useState } from 'react';
import { useAppDispatch } from '../hooks';
import { loginUser, signupUser } from '../reducers/userReducer';
import { Formik, Form, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { TextInput } from './FormikMuiFields';
import TermsConsent from './TermsConsent';
import { notify } from '../reducers/notificationReducer';
import AlertMessage from './AlertMessage';
import getErrorMsg from '../utils/getErrorMsg';
import {
  Button,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface AuthFormValues {
  username: string;
  password: string;
}

const validationSchemaSignup = yup.object({
  username: yup
    .string()
    .required('Required')
    .max(20, 'Must be at most 20 characters')
    .min(3, 'Must be at least 3 characters')
    .matches(/^[a-zA-Z0-9-_]*$/, 'Only alphanumeric characters allowed, no spaces/symbols'),

  password: yup.string().required('Required').min(6, 'Must be at least 6 characters'),
});

const validationSchemaLogin = yup.object({
  username: yup.string().required('Required'),
  password: yup.string().required('Required'),
});

const FormikTextInput = TextInput as React.ComponentType<any>;

interface AuthFormProps {
  /** Closes the surrounding modal, e.g. when a policy link is followed. */
  onClose?: () => void;
}

const AuthForm: FC<AuthFormProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const [agreed, setAgreed] = useState(false);
  const [authType, setAuthType] = useState<'login' | 'signup'>('login');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  const authWrapperSx = {
    display: 'flex',
    flexDirection: authType === 'login' ? 'row' : 'row-reverse',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginTop: 'var(--space-auth-mobile-offset)',
    },
  };

  const formSx = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: 2.5,
    paddingRight: 2.5,
    paddingBottom: 2.5,
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  };

  const formTitleSx = { textAlign: 'center', [theme.breakpoints.down('sm')]: { fontSize: 'mobileTitle' } };
  const switchTextSx = {
    textAlign: 'center',
    marginBottom: 'var(--space-form-switch-gap)',
    [theme.breakpoints.down('sm')]: { marginBottom: 0, fontSize: 'body' },
  };
  const submitButtonSx = { marginTop: 'var(--space-stack-xlarge)' };
  const inputSx = { display: 'flex', alignItems: 'flex-end' };
  const inputIconSx = { marginRight: 1 };
  const sidePanelSx = { padding: 2.5, margin: 'auto 0' };
  const dividerSx = { marginLeft: 5, marginRight: 5 };

  const handleLogin = async (
    values: AuthFormValues,
    { setSubmitting }: FormikHelpers<AuthFormValues>
  ) => {
    try {
      setSubmitting(true);
      const credentials = { ...values } as Record<string, unknown>;
      await dispatch(loginUser(credentials));
      dispatch(notify(`Welcome, ${values.username}. You're logged in!`, 'success'));
    } catch (err) {
      setSubmitting(false);
      setError(getErrorMsg(err));
    }
  };

  const handleSignup = async (
    values: AuthFormValues,
    { setSubmitting }: FormikHelpers<AuthFormValues>
  ) => {
    try {
      setSubmitting(true);
      const credentials = { ...values, acceptedTerms: agreed } as Record<string, unknown>;
      await dispatch(signupUser(credentials));
      dispatch(
        notify(`Welcome, ${values.username}. You've been successfully registered.`, 'success')
      );
    } catch (err) {
      setSubmitting(false);
      setError(getErrorMsg(err));
    }
  };

  return (
    <div>
      <Box sx={authWrapperSx}>
        <Formik
          validateOnChange
          initialValues={{ username: '', password: '' }}
          onSubmit={authType === 'login' ? handleLogin : handleSignup}
          validationSchema={authType === 'login' ? validationSchemaLogin : validationSchemaSignup}
        >
          {({ isSubmitting }) => (
            <>
              <Box component={Form} sx={formSx}>
                <Typography variant="h5" color="secondary" sx={formTitleSx}>
                  {authType === 'login' ? 'Login to your account' : 'Create a new account'}
                </Typography>

                <Box sx={inputSx}>
                  <PersonIcon sx={inputIconSx} color="primary" />
                  <FormikTextInput name="username" type="text" placeholder="Enter username" label="Username" required fullWidth />
                </Box>

                <Box sx={inputSx}>
                  <LockIcon sx={inputIconSx} color="primary" />
                  <FormikTextInput
                    name="password"
                    type={showPass ? 'text' : 'password'}
                    placeholder="Enter password"
                    label="Password"
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPass((p) => !p)} size="large">
                            {showPass ? <VisibilityOffIcon color="primary" /> : <VisibilityIcon color="primary" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {authType === 'signup' && (
                  <TermsConsent checked={agreed} onChange={setAgreed} onNavigate={onClose} />
                )}

                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  size="large"
                  startIcon={authType === 'login' ? <ExitToAppIcon /> : <PersonAddIcon />}
                  sx={submitButtonSx}
                  disabled={isSubmitting || (authType === 'signup' && !agreed)}
                >
                  {authType === 'login' ? (isSubmitting ? 'Logging In' : 'Login') : isSubmitting ? 'Signing Up' : 'Sign Up'}
                </Button>
              </Box>

              <Divider orientation="vertical" flexItem sx={dividerSx} />

              <Box sx={sidePanelSx}>
                <Typography variant="h6" sx={switchTextSx} color="primary">
                  {authType === 'login' ? `Don't have an account?` : 'Already have an account?'}
                </Typography>
                <Button
                  onClick={() => setAuthType(authType === 'login' ? 'signup' : 'login')}
                  fullWidth
                  size="large"
                  color="primary"
                  variant="outlined"
                  startIcon={authType === 'login' ? <PersonAddIcon /> : <ExitToAppIcon />}
                  disabled={isSubmitting}
                >
                  {authType === 'login' ? 'Sign Up' : 'Login'}
                </Button>
              </Box>
            </>
          )}
        </Formik>
      </Box>

      <div>
        <AlertMessage error={error} severity="error" clearError={() => setError(null)} />
      </div>
    </div>
  );
};

export default AuthForm;
